import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

const API_BASE_URL = process.env.REACT_APP_API_URL || '';

const generateUUID = () => {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = Math.random() * 16 | 0;
    return (c === 'x' ? r : (r & 0x3 | 0x8)).toString(16);
  });
};

const getVisitorId = () => {
  let id = localStorage.getItem('visitor_id');
  if (!id) {
    try {
      id = crypto.randomUUID ? crypto.randomUUID() : generateUUID();
    } catch (e) {
      id = generateUUID();
    }
    localStorage.setItem('visitor_id', id);
  }
  return id;
};

const getSessionId = () => {
  let id = sessionStorage.getItem('session_id');
  if (!id) {
    try {
      id = crypto.randomUUID ? crypto.randomUUID() : generateUUID();
    } catch (e) {
      id = generateUUID();
    }
    sessionStorage.setItem('session_id', id);
  }
  return id;
};

export const trackEvent = async (eventType = 'page_view', pagePath = null) => {
  try {
    const visitorId = getVisitorId();
    const sessionId = getSessionId();

    const response = await fetch(`${API_BASE_URL}/api/analytics/visit`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Visitor-ID': visitorId,
      },
      body: JSON.stringify({
        session_id: sessionId,
        page_path: pagePath || window.location.pathname,
        referrer: document.referrer || null,
        screen_width: window.screen.width,
        screen_height: window.screen.height,
        language: navigator.language || 'en',
        event_type: eventType,
      }),
    });

    if (!response.ok) {
      const data = await response.json();
      throw new Error(data.detail || 'Analytics request failed');
    }

    const data = await response.json();
    if (data.visitor_id && data.visitor_id !== visitorId) {
      localStorage.setItem('visitor_id', data.visitor_id);
    }

    return data;
  } catch (error) {
    if (process.env.NODE_ENV === 'development') {
      console.warn('Analytics error:', error.message);
    }
    return null;
  }
};

export const useAnalytics = () => {
  const location = useLocation();
  const initialTracked = useRef(false);

  useEffect(() => {
    if (initialTracked.current) {
      trackEvent('page_view', location.pathname);
    } else {
      initialTracked.current = true;
      const timer = setTimeout(() => {
        trackEvent('page_view', location.pathname);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [location.pathname]);

  return { trackEvent };
};

export default useAnalytics;
